import { getBestDecisions } from "@beyourbest/decision-engine";
import { ReplayPassType, type ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import { loadReplayInWorker } from "@hooks/game/activity/session/replay-loader-worker-client";
import type { ReplayScenarioTarget } from "@hooks/game/activity/session/session-results.utils";
import type { ReplayData } from "@Shared/types/ReplayData";

export type { ReplayScenarioTarget };

const TOP_DECISIONS_COUNT = 3;

export const buildScenarioKey = (tab: number, cognitiveResultId: number) => `${tab}:${cognitiveResultId}`;

export async function computeReplayDecisions(data: ReplayData): Promise<ReplayDecision[] | null> {
  if (!data.ball) {
    return [];
  }

  try {
    const bestDecisionsResponse = await getBestDecisions(data as Parameters<typeof getBestDecisions>[0], {
      topDecisionsCount: TOP_DECISIONS_COUNT,
      excludeDecisionTypes: [ReplayPassType.DRIBBLE],
    });

    if (bestDecisionsResponse.success) {
      return bestDecisionsResponse.data;
    }

    console.error("Failed to compute replay decisions from decision engine response:", bestDecisionsResponse.error);
  } catch (error) {
    console.error("Failed to compute replay decisions from unexpected exception:", error);
  }

  return null;
}

interface LoadReplayPayloadParams {
  resultId: number;
  replayUserId: string;
  requestReplay: (payload: { cognitive_result_id: number; user_id: string }) => Promise<{ download_url?: string } | undefined>;
  onDownloadUrl?: (downloadUrl: string) => void;
}

export async function loadReplayPayload({
  resultId,
  replayUserId,
  requestReplay,
  onDownloadUrl,
}: LoadReplayPayloadParams): Promise<ReplayData> {
  const response = await requestReplay({
    cognitive_result_id: resultId,
    user_id: replayUserId,
  });

  const downloadUrl = response?.download_url;
  if (!downloadUrl) {
    throw new Error("Failed to get download URL for replay");
  }

  onDownloadUrl?.(downloadUrl);

  return loadReplayInWorker(downloadUrl);
}
